/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import IScene from "../scene_interface";
import {vec3} from "gl-matrix";
import Element from "./element"
import {EEmbededElementTypes, Utils} from "js_renderer";
import Factory from "./factory";

export default
class Line extends Element {

    public static TypeName: string = EEmbededElementTypes.LINE;
    private _positionA: vec3;
    private _positionB: vec3;
    private thin: boolean;

    constructor(aSceneAPI: IScene) {
        super(aSceneAPI);

        this._positionA = vec3.create();
        this._positionB = vec3.create();

        this.extendProperties({
            color: [1.0, 0.0, 0.0],
            alwaysOnTop: false,
            pickable: true,
            contributeToNearPlaneClipping: true,
            notOutline: false
        });

        this.thin = true; // this enables rendering the line as line primitive in opengl instead of generation geometry for it
    }

    public static create(aSceneAPI: IScene) {
        let element = new Line(aSceneAPI);
        element._typeName = Line.TypeName;
        return element;
    }

    public setPositions(aPositionA, aPositionB) {
        this._positionA = aPositionA;
        this._positionB = aPositionB;

        this.updatePositions([
            new Float32Array([
                this._positionA[0], this._positionA[1], this._positionA[2],
                this._positionB[0], this._positionB[1], this._positionB[2]
            ])
        ]);

        this._sceneAPI.viewModeModule.scheduleRedraw();
    }

    /**
     * Return copy of the start and end position of the line in 3D scene.
     *
     * @returns {vec2<vec3, vec3>} copy of line position
     */
    public getPositions() {
        return [vec3.clone(this._positionA), vec3.clone(this._positionB)];
    }

    protected _generateElementDescriptionJSON(): any {
        return {
            "type": this.getTypeName(),        // line is different from curve as it has only two points
            "id": this.getTypeName() + "_" + this._UID,   // this is is not important in the engine, it can identify e.g. id of an element in Lexocad
            "UID": this._UID//,
        };
    }

    private _generateBinaryData(): ArrayBuffer {
        let binarydata = Utils.mergeBuffers(this._positionA.buffer, this._positionB.buffer);
        binarydata = Utils.mergeBuffers(binarydata.buffer, (new Uint32Array([0, 1])).buffer);

        return binarydata.buffer;
    }

    /***
     * We generate the base64 encoded binary data here as we would like to use the binaryGeometry class which accepts as input a response which is an array with data
     * @returns {{description: {id: string, type: string, bboxMin: *, bboxMax: *, color: Array}, appearance: {renderOption: {alwaysOnTop: boolean, pickable: boolean}}, binaryGeometry: {vertexCount: number, indexCount: number, offset: number, binSize: number, src: string}}}
     * @private
     */
    protected _generateGeometryObjectDescriptionJSON(): any {
        return [{
            description: {
                type: this.thin ? "OUTLINE" : "LINE",
                bboxMin: [this._positionA[0], this._positionA[1], this._positionA[2]],
                bboxMax: [this._positionB[0], this._positionB[1], this._positionB[2]],
                color: this._properties.color,
                contributeToNearPlaneClipping: this._properties.contributeToNearPlaneClipping
            },
            appearance: {
                renderOption: {
                    alwaysOnTop: this._properties.alwaysOnTop,
                    pickable: this._properties.pickable,
                    notOutline: this._properties.notOutline
                }
            },
            binaryGeometry: {
                vertexCount: 2,
                indexCount: 2,
                offset: 0,
                binSize: 32,
                src: this._renderDocument.getRenderer().getDataStorage().createLocalResourceFromArrayBuffer(this._generateBinaryData())
            }
        }];
    }
}

Factory.register(Line);